import React, { useEffect, useState } from "react"
import {
  Row,
  Col,
  Tabs,
  Space,
  Divider,
  Card,
  Typography,
  Spin,
  Progress,
  Statistic,
  Flex,
} from "antd"
import StatCard from "../components/StatCard"
import QuickActions from "../components/QuickActions"
import NotificationPanel from "../components/NotificationPanel"
import EngPaymentDashboard from "../components/EngPaymentDashboard"
import EngSummaryFinance from "../../Accounting/engSummary"
import { getEngineerDashboardApi } from "helpers/backend_helper"
import {
  ProjectOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  WalletOutlined,
  FolderOutlined,
  LineChartOutlined,
  FileSearchOutlined,
  FileDoneOutlined,
  FileExclamationOutlined,
  EditOutlined,
  FileImageOutlined,
  HourglassOutlined,
  EyeOutlined,
  SafetyOutlined,
  DollarOutlined,
  BankOutlined,
  RiseOutlined,
  FallOutlined,
  SwapOutlined,
  CalculatorOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  MinusCircleOutlined,
  DollarCircleOutlined,
} from "@ant-design/icons"

const { Title, Text } = Typography

// Icon and color mapping for project levels (0-7)
const projectLevelConfig = {
  0: {
    icon: <HourglassOutlined />,
    color: "#faad14",
    bgColor: "#fffbe6",
    borderColor: "#ffe58f",
    name: "انتظارتاییدطراحی",
  },
  1: {
    icon: <EditOutlined />,
    color: "#52c41a",
    bgColor: "#f6ffed",
    borderColor: "#b7eb8f",
    name: "طراحی انجام شده",
  },
  2: {
    icon: <ClockCircleOutlined />,
    color: "#722ed1",
    bgColor: "#f9f0ff",
    borderColor: "#d3adf7",
    name: "انتظارتاییدنقشه",
  },
  3: {
    icon: <FileImageOutlined />,
    color: "#1890ff",
    bgColor: "#e6f7ff",
    borderColor: "#91d5ff",
    name: "نقشه انجام شده",
  },
  4: {
    icon: <FileExclamationOutlined />,
    color: "#ff4d4f",
    bgColor: "#fff2f0",
    borderColor: "#ffccc7",
    name: "نقص",
  },
}

const EngineerDashboard = ({ infoMessage, telegramLink, userInfo }) => {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  // Fetch dashboard data from backend
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        const response = await getEngineerDashboardApi()
        setDashboardData(response)
      } catch (error) {
        console.error("Failed to fetch engineer dashboard data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  // Calculate total projects
  const totalProjects =
    dashboardData?.projectCounts?.reduce((sum, item) => sum + item.count, 0) ||
    0

  // Get engWorkInfo from API response
  const engWorkInfo = dashboardData?.engWorkInfo || null

  // Format number with thousand separator
  const formatNumber = num => {
    if (num === null || num === undefined) return "0"
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  const coerceNumber = value => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : 0
  }

  const formatSignedNumber = value => {
    const numericValue = coerceNumber(value)
    const absValue = Math.abs(numericValue)
    const formatted = formatNumber(absValue)
    return numericValue < 0 ? `-${formatted}` : formatted
  }

  const renderRialBalanceRow = (label, value) => {
    const numericValue = coerceNumber(value)
    const isNegative = numericValue < 0

    return (
      <Flex align="center" gap={6}>
        <span style={{ color: "#fff" }}>{label} :</span>
        {isNegative && (
          <FallOutlined style={{ color: "#ffccc7", fontSize: 12 }} />
        )}
        <span
          style={{
            color: isNegative ? "#ffccc7" : "#fff",
            direction: "ltr",
            unicodeBidi: "embed",
            fontWeight: 600,
          }}
        >
          {formatSignedNumber(numericValue)}
        </span>
        {isNegative && (
          <span
            style={{
              padding: "0 6px",
              border: "1px solid rgba(255, 77, 79, 0.45)",
              background: "rgba(255, 77, 79, 0.15)",
              color: "#ffccc7",
              borderRadius: 10,
              fontSize: 10,
              lineHeight: "18px",
            }}
          >
            منفی
          </span>
        )}
      </Flex>
    )
  }

  // Build stats from API response
  const stats =
    dashboardData?.projectCounts?.filter(f=>f.projectLevel !== 1).map(item => {
      const levelConfig =
        projectLevelConfig[item.projectLevel] || projectLevelConfig[0]

      return {
        title: item.projectLevelName,
        value: item.count,
        icon: levelConfig.icon,
        suffix: "پرونده",
        style: {
          background: levelConfig.bgColor,
          borderColor: levelConfig.borderColor,
          color: levelConfig.color,
        },
      }
    }) || []

  // تعریف آیتم‌های تب
  const tabItems = [
    {
      key: "1",
      label: (
        <Space>
          <AppstoreOutlined />
          نمای کلی
        </Space>
      ),
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <QuickActions role="Engineer" />
          </Col>
          <Col xs={24} lg={16}>
            <NotificationPanel
              telegramLink={telegramLink}
              showTelegram={true}
              maxItems={5}
            />
          </Col>
        </Row>
      ),
    },
    {
      key: "2",
      label: (
        <Space>
          <WalletOutlined />
          پرداخت‌های من
        </Space>
      ),
      children: <EngPaymentDashboard />,
    },
    // {
    //   key: "3",
    //   label: (
    //     <Space>
    //       <FolderOutlined />
    //       پرونده‌های من
    //     </Space>
    //   ),
    //   children: (
    //     <Row gutter={[16, 16]}>
    //       <Col xs={24}>
    //         <StatCard
    //           title="لیست پرونده‌های من"
    //           value="به زودی"
    //           icon={<ProjectOutlined />}
    //         />
    //       </Col>
    //     </Row>
    //   ),
    // },
    {
      key: "4",
      label: (
        <Space>
          <BarChartOutlined />
          خلاصه کارکرد
        </Space>
      ),
      children: (
        <Card
          title={
            <Space>
              <BarChartOutlined style={{ color: "#1890ff" }} />
              <span>خلاصه کارکرد سالانه</span>
            </Space>
          }
          style={{ borderRadius: 12 }}
          styles={{ body: { padding: 16 } }}
        >
          <EngSummaryFinance userInfo={userInfo} />
        </Card>
      ),
    },
    {
      key: "5",
      label: (
        <Space>
          <LineChartOutlined />
          آمار عملکرد
        </Space>
      ),
      children: (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <StatCard
              title="میانگین زمان طراحی"
              value="در حال پیاده سازی"
              suffix="روز"
              icon={<ToolOutlined />}
            />
          </Col>
          <Col xs={24} md={12}>
            <StatCard
              title="نرخ تایید"
              value="در حال پیاده سازی"
              suffix="%"
              icon={<CheckCircleOutlined />}
              trend="up"
            />
          </Col>
        </Row>
      ),
    },
  ]

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: "100%", padding: 8 }}
    >
      {/* Statistics Cards */}
      <Spin spinning={loading} tip="در حال بارگذاری آمار...">
        <Row gutter={[16, 16]}>
          {/* Total and remain Projects Card */}
          <Col xs={12} sm={12} lg={8}>
            <Card
              style={{
                borderRadius: 12,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
                height: "100%",
              }}
              styles={{ body: { padding: 20 } }}
            >
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Space  align="center">
                  <ProjectOutlined style={{ fontSize: 24, color: "#fff" }} />
                  <Title level={5} style={{ color: "#fff", margin: 0 }}>
                    مانده ها - ریال
                  </Title>
                </Space>
                {renderRialBalanceRow("کل", engWorkInfo?.totalQuotaBalanceWithAddAndBurn)}
                {renderRialBalanceRow("طراحی", engWorkInfo?.remainDesignFee)}
                {renderRialBalanceRow("نقشه", engWorkInfo?.remainMapFee)}
              </Space>
            </Card>
          </Col>

          {/* Dynamic Stats from API */}
          {stats.map((stat, index) => (
            <Col xs={12} sm={12} lg={4} key={index}>
              <Card
                style={{
                  borderRadius: 12,
                  border: `1px solid ${stat.style?.borderColor || "#f0f0f0"}`,
                  background: stat.style?.bgColor || "#fff",
                  height: "100%",
                }}
                styles={{ body: { padding: 20 } }}
              >
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Space align="center">
                    <span
                      style={{
                        fontSize: 20,
                        color: stat.style?.color || "#1890ff",
                      }}
                    >
                      {stat.icon}
                    </span>
                    <Text type="secondary">{stat.title}</Text>
                  </Space>
                  <Space align="baseline">
                    <Title
                      level={2}
                      style={{ margin: "12px 0 0", color: stat.style?.color }}
                    >
                      {stat.value}
                    </Title>
                    <Text type="secondary">{stat.suffix}</Text>
                  </Space>
                </Space>
              </Card>
            </Col>
          ))}

          {/* Engineer Work Info - Financial Stats */}
          {engWorkInfo && (
            <>
              {/* تخصیص این دوره */}
              <Col xs={12} sm={12} md={8} lg={4}>
                <Card
                  size="small"
                  style={{
                    borderRadius: 12,
                    border: "1px solid #e6f7ff",
                    background: "#e6f7ff",
                    height: "100%",
                  }}
                  styles={{ body: { padding: 16 } }}
                >
                  <Statistic
                    title={
                      <Flex align="center" gap={6}>
                        <SwapOutlined style={{ color: "#1890ff" }} />
                        <span>تخصیص این دوره</span>
                      </Flex>
                    }
                    value={engWorkInfo.sumAmountEngQuota}
                    formatter={value => formatNumber(value)}
                    suffix={
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        ریال
                      </Text>
                    }
                    valueStyle={{ fontSize: 18, color: "#1890ff" }}
                  />
                </Card>
              </Col>

              {/* کسورات این دوره */}
              <Col xs={12} sm={12} md={8} lg={4}>
                <Card
                  size="small"
                  style={{
                    borderRadius: 12,
                    border: "1px solid #fff2f0",
                    background: "#fff2f0",
                    height: "100%",
                  }}
                  styles={{ body: { padding: 16 } }}
                >
                  <Statistic
                    title={
                      <Flex align="center" gap={6}>
                        <MinusCircleOutlined style={{ color: "#ff4d4f" }} />
                        <span>کسورات این دوره</span>
                      </Flex>
                    }
                    value={engWorkInfo.sumAmountEngQuotaBurn}
                    formatter={value => formatNumber(value)}
                    suffix={
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        ریال
                      </Text>
                    }
                    valueStyle={{ fontSize: 18, color: "#ff4d4f" }}
                  />
                </Card>
              </Col>

              {/* تخصیص با کسر کسورات */}
              <Col xs={12} sm={12} md={8} lg={4}>
                <Card
                  size="small"
                  style={{
                    borderRadius: 12,
                    border: "1px solid #f6ffed",
                    background: "#f6ffed",
                    height: "100%",
                  }}
                  styles={{ body: { padding: 16 } }}
                >
                  <Statistic
                    title={
                      <Flex align="center" gap={6}>
                        <DollarCircleOutlined style={{ color: "#52c41a" }} />
                        <span>تخصیص با کسر کسورات</span>
                      </Flex>
                    }
                    value={
                      engWorkInfo.sumAmountEngQuota -
                      engWorkInfo.sumAmountEngQuotaBurn
                    }
                    formatter={value => formatNumber(value)}
                    suffix={
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        ریال
                      </Text>
                    }
                    valueStyle={{ fontSize: 18, color: "#52c41a" }}
                  />
                </Card>
              </Col>

              {/* ارجاعی این دوره */}
              <Col xs={12} sm={12} md={8} lg={4}>
                <Card
                  size="small"
                  style={{
                    borderRadius: 12,
                    border: "1px solid #fff7e6",
                    background: "#fff7e6",
                    height: "100%",
                  }}
                  styles={{ body: { padding: 16 } }}
                >
                  <Statistic
                    title={
                      <Flex align="center" gap={6}>
                        <FileTextOutlined style={{ color: "#fa8c16" }} />
                        <span>ارجاعی این دوره</span>
                      </Flex>
                    }
                    value={engWorkInfo.sumAmountEngProcessFee}
                    formatter={value => formatNumber(value)}
                    suffix={
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        ریال
                      </Text>
                    }
                    valueStyle={{ fontSize: 18, color: "#fa8c16" }}
                  />
                </Card>
              </Col>

              {/* انجام شده این دوره */}
              <Col xs={12} sm={12} md={8} lg={4}>
                <Card
                  size="small"
                  style={{
                    borderRadius: 12,
                    border: "1px solid #f6ffed",
                    background: "#f6ffed",
                    height: "100%",
                  }}
                  styles={{ body: { padding: 16 } }}
                >
                  <Statistic
                    title={
                      <Flex align="center" gap={6}>
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                        <span>انجام شده این دوره</span>
                      </Flex>
                    }
                    value={engWorkInfo.sumAmountEngRealWorkThisQuarter}
                    formatter={value => formatNumber(value)}
                    suffix={
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        ریال
                      </Text>
                    }
                    valueStyle={{ fontSize: 18, color: "#52c41a" }}
                  />
                </Card>
              </Col>

              {/* فاکتورهای پرداخت نشده */}
              <Col xs={12} sm={12} md={8} lg={4}>
                <Card
                  size="small"
                  style={{
                    borderRadius: 12,
                    border: "1px solid #f9f0ff",
                    background: "#f9f0ff",
                    height: "100%",
                  }}
                  styles={{ body: { padding: 16 } }}
                >
                  <Statistic
                    title={
                      <Flex align="center" gap={6}>
                        <CreditCardOutlined style={{ color: "#722ed1" }} />
                        <span>فاکتورهای پرداخت نشده</span>
                      </Flex>
                    }
                    value={engWorkInfo.engAmountNotPayment}
                    formatter={value => formatNumber(value)}
                    suffix={
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        ریال
                      </Text>
                    }
                    valueStyle={{ fontSize: 18, color: "#722ed1" }}
                  />
                </Card>
              </Col>
            </>
          )}
        </Row>

        {/* Progress Overview */}
        {/* {dashboardData?.projectCounts &&
          dashboardData.projectCounts.length > 0 && (
            <Card
              style={{ borderRadius: 12, marginTop: 16 }}
              styles={{ body: { padding: 24 } }}
            >
              <Title level={5} style={{ marginBottom: 16 }}>
                <BarChartOutlined style={{ marginLeft: 8 }} />
                نمای کلی وضعیت پرونده‌ها
              </Title>
              <Row gutter={[24, 16]}>
                {dashboardData.projectCounts.map((item, index) => {
                  const percentage =
                    totalProjects > 0
                      ? Math.round((item.count / totalProjects) * 100)
                      : 0
                  const levelConfig =
                    projectLevelConfig[item.projectLevel] ||
                    projectLevelConfig[0]

                  return (
                    <Col xs={24} sm={8} md={4} key={index}>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Space
                          align="center"
                          style={{
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Text>{item.projectLevelName}</Text>
                          <Text strong>{item.count}</Text>
                        </Space>
                        <Progress
                          percent={percentage}
                          strokeColor={levelConfig.color}
                          size="small"
                          format={percent => `${percent}%`}
                        />
                      </Space>
                    </Col>
                  )
                })}
              </Row>
            </Card>
          )} */}
      </Spin>

      {/* Main Content */}
      <Tabs
        defaultActiveKey="1"
        size="large"
        type="card"
        items={tabItems}
        destroyInactiveTabPane={true}
      />
    </Space>
  )
}

export default EngineerDashboard
